"use client";

import React, { useState, useEffect } from "react";
import { motion, type Variants } from "motion/react";
import { Check, Zap, ShieldCheck, HeartHandshake, ArrowRight, Download, Monitor, Code } from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

const pricingPlans = [
    {
        name: "Basic Version",
        price: "₹0",
        period: "Forever",
        desc: "Everything you need for a single outlet to thrive.",
        features: ["Core POS & Billing", "Inventory Management", "Menu Customization", "Real-time Sales", "Single Outlet Support"],
        cta: "Download Now",
        popular: false,
        isDownload: true,
    },
    {
        name: "Custom Plan",
        price: "Custom",
        period: "Quote",
        desc: "Pricing based entirely on your specific business requirements.",
        features: ["Custom UI Themes", "Connection With Website", "Additional Screen Setup", "Custom Feature Additions", "Dedicated Support"],
        cta: "Contact Us",
        popular: true,
    },
];

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export function PricingContent() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    
    return (
        <main className="relative min-h-screen flex flex-col justify-center pt-28 pb-12 px-4 overflow-x-hidden bg-background text-foreground transition-colors duration-500 perspective-[1500px]">
            {!mounted ? null : (
                <>
                    {/* Background Layering — Matching Home/Hero */}
                    <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
                        <div className="absolute inset-0 grid-pattern opacity-[0.3] dark:opacity-[0.15]" />
                        
                        {/* Architectural Blueprint Lines */}
                        <div className="absolute top-0 left-[20%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[40%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[60%] w-px h-full bg-border/40" />
                        <div className="absolute top-0 left-[80%] w-px h-full bg-border/40" />
                        <div className="absolute top-[20%] left-0 w-full h-px bg-border/40" />
                        <div className="absolute top-[50%] left-0 w-full h-px bg-border/40" />
                        
                        {/* Blueprint Labels */}
                        <div className="absolute top-4 left-4 text-[10px] text-muted-foreground font-mono tracking-widest uppercase">Pricing.Arch // v1.0</div>
                        <div className="absolute bottom-4 right-4 text-[10px] text-muted-foreground font-mono tracking-widest uppercase md:block hidden">System.Pricing_Matrix</div>
                        
                        {/* Gradient Fades */}
                        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background/90" />
                    </div>

                    <div className="relative z-10 max-w-7xl mx-auto">
                        {/* Technical Badge — Matching Home */}
                        <div className="flex justify-center mb-10">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-flex items-center gap-3 px-3 py-1 rounded-lg bg-surface border border-border-subtle shadow-sm text-xs font-mono tracking-tighter group cursor-default"
                            >
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                                </div>
                                <span className="text-muted-foreground">PRICING_MODEL:</span>
                                <span className="text-primary font-bold">TRANSPARENT</span>
                            </motion.div>
                        </div>

                        {/* Headings — Matching Home Typography */}
                        <div className="text-center mb-8 md:mb-12">
                            <h1 className="text-5xl md:text-7xl font-semibold tracking-tight text-foreground mb-4 leading-[0.9] flex flex-col items-center">
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    Access is
                                </motion.span>
                                <motion.span
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                                    className="text-gradient font-bold"
                                >
                                    Always Free.
                                </motion.span>
                            </h1>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className="max-w-2xl mx-auto text-xl text-muted-foreground font-normal tracking-tight leading-relaxed"
                            >
                                Core restaurant infrastructure cost zero.
                                <span className="text-foreground block mt-1 font-medium">Pay only for custom scale & features.</span>
                            </motion.p>
                        </div>

                        {/* Pricing Cards — Compacted for no-scroll */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
                            {pricingPlans.map((plan, i) => (
                                <motion.div
                                    key={i}
                                    initial="hidden"
                                    animate="show"
                                    variants={itemVariants}
                                    transition={{ delay: 0.4 + i * 0.1 }}
                                    className={cn(
                                        "relative rounded-[2.5rem] p-8 md:p-12 border bg-surface/50 backdrop-blur-md overflow-hidden transition-all duration-500",
                                        plan.popular 
                                            ? "border-primary/50 shadow-[0_20px_60px_-20px_rgba(204,252,69,0.15)] ring-1 ring-primary/20" 
                                            : "border-border-subtle hover:border-border shadow-[0_20px_60px_rgba(0,0,0,0.05)] dark:shadow-none"
                                    )}
                                >
                                    {/* Ambient Glow for Recommended */}
                                    {plan.popular && (
                                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/4" />
                                    )}

                                    {plan.popular && (
                                        <div className="absolute top-8 right-8 flex items-center gap-1.5 bg-primary/10 text-primary text-[9px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded border border-primary/20">
                                            <Code size={10} /> Scalable
                                        </div>
                                    )}

                                    <h3 className="text-xl md:text-2xl font-semibold tracking-tight mb-2 text-foreground">{plan.name}</h3>
                                    <div className="flex items-baseline gap-2 mb-4">
                                        <span className="text-4xl md:text-5xl font-bold tracking-tighter text-foreground">{plan.price}</span>
                                        <span className="text-xs text-muted-foreground font-semibold uppercase tracking-widest">/ {plan.period}</span>
                                    </div>
                                    <p className="text-xs md:text-sm text-muted-foreground mb-6 leading-relaxed font-normal">
                                        {plan.desc}
                                    </p>

                                    {/* Features List */}
                                    <div className="space-y-3 mb-8 relative z-10">
                                        {plan.features.map((feat, fi) => (
                                            <div key={fi} className="flex items-center gap-4">
                                                <div className="w-6 h-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                                                    <Check size={12} strokeWidth={3} />
                                                </div>
                                                <span className="text-sm font-medium tracking-tight text-foreground/90">{feat}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* CTAs */}
                                    <div className="relative z-10 w-full">
                                        {plan.isDownload ? (
                                            <a
                                                href="https://apps.microsoft.com/store/detail/9P9W500L6KWG?cid=DevShareMCLPCS"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: "none" }}
                                                className="block w-full"
                                            >
                                                <button className={cn(
                                                    "w-full px-6 py-4 rounded-xl font-bold border transition-all flex items-center justify-between group shadow-sm",
                                                    "bg-foreground text-background hover:scale-[1.02]", plan.popular ? "border-primary/50" : "border-border-subtle"
                                                )}>
                                                    <div className="flex flex-col items-start leading-tight text-left">
                                                        <span className="text-[9px] uppercase tracking-widest opacity-60 mb-0.5">Download for Windows</span>
                                                        <span className="text-[15px]">Microsoft Store</span>
                                                    </div>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="20" height="20" aria-label="Microsoft logo" className="flex-shrink-0 group-hover:scale-110 transition-transform">
                                                        <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
                                                        <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
                                                        <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
                                                        <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                                                    </svg>
                                                </button>
                                            </a>
                                        ) : (
                                            <div className="flex flex-col gap-2">
                                                <a href="mailto:admin@doodledeveloper.com" className="block w-full">
                                                    <button
                                                        className={cn(
                                                            "w-full py-4 rounded-xl font-bold text-sm tracking-tight transition-all active:scale-[0.98] flex items-center justify-center gap-2",
                                                            plan.popular 
                                                                ? "bg-primary text-primary-foreground hover:scale-[1.02] shadow-[0_15px_30px_-10px_rgba(204,252,69,0.3)]" 
                                                                : "bg-surface border border-border-subtle text-foreground hover:bg-muted"
                                                        )}
                                                    >
                                                        {plan.cta} <ArrowRight size={14} />
                                                    </button>
                                                </a>
                                                <div className="text-center mt-2">
                                                    <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest">Or Call: <a href="tel:8221955036" className="text-foreground hover:text-primary transition-colors">8221955036</a></span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Social Proof Banner — Updated to sleek glassmorphism */}
                        <div className="rounded-2xl border border-border-subtle bg-surface/30 backdrop-blur-md overflow-hidden max-w-4xl mx-auto py-6 px-4 md:px-8 shadow-sm">
                            <div className="grid grid-cols-4 gap-4 text-center items-center">
                                {[
                                    { icon: Zap, label: "Fastest Setup" },
                                    { icon: ShieldCheck, label: "Cloud Backups" },
                                    { icon: HeartHandshake, label: "Community" },
                                    { icon: Monitor, label: "Any Hardware" }
                                ].map((item, i) => (
                                    <div key={i} className="flex flex-col items-center gap-4 opacity-70 hover:opacity-100 transition-all cursor-default">
                                        <div className="w-10 h-10 rounded-xl bg-background border border-border-subtle flex items-center justify-center text-primary shadow-sm">
                                            <item.icon className="w-5 h-5" />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </main>
    );
}

